/**
 * "Registrar limitação" ao lado do concluir (E162.F2.T02, QA-18, ADR-0111 §2.6).
 *
 * A trava E147.F2.C01 recusa concluir a CCB com consulta do sistema sem resposta nem limitação, e a
 * saída ficava na Conferência das etapas da pré-análise: o perito ia e voltava entre as duas telas.
 * Aqui a mesma régua do servidor (as consultas que travam) e as mesmas duas saídas — tentar de novo
 * ou registrar a limitação com justificativa —, logo abaixo da decisão. Sem consulta travada, nada.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { ConsultaIndisponivel } from '@/components/ConferenciaDasEtapas/ConsultaIndisponivel';
import { t } from '@/components/ConferenciaDasEtapas/textos';
import { consultasTravadas } from '@/lib/conferencia_das_etapas';
import { useConferenciaDasEtapas } from '@/lib/useConferenciaDasEtapas';

export interface ConsultasQueTravamProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando a mesa relê o caso: as consultas são lidas de novo. */
  releitura?: unknown;
  /** A limitação ou a nova tentativa mudou o caso: a mesa relê. */
  onMudou: () => void;
}

export function ConsultasQueTravam({
  client,
  caseId,
  releitura,
  onMudou,
}: ConsultasQueTravamProps): JSX.Element | null {
  const { etapas, pedir } = useConferenciaDasEtapas(client, caseId, releitura);
  const [aviso, setAviso] = useState<string | null>(null);
  const consultas =
    etapas.estado === 'pronto' && etapas.dados.conferida ? consultasTravadas(etapas.dados) : [];
  if (consultas.length === 0 && aviso === null) return null;
  return (
    <section className="space-y-sc-2" data-testid="mesa-consultas-que-travam">
      <p role="status" className="text-body-sm text-fg" data-testid="mesa-consultas-aviso">
        {aviso ?? ''}
      </p>
      {consultas.length > 0 ? (
        <>
          <h3 className="text-body-md font-semibold text-fg">{t.consultasTitulo}</h3>
          <p className="text-body-sm text-fg-muted">{t.consultasTexto}</p>
          <ul className="space-y-sc-2">
            {consultas.map((c) => (
              <ConsultaIndisponivel
                key={c.provedor}
                consulta={c}
                pedir={pedir}
                onFeito={(texto) => {
                  setAviso(texto);
                  onMudou();
                }}
              />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
